// dependencies
import { endOfDay } from "date-fns";
import React from "react";
import { useNavigate } from "react-router-dom";

import { ProgressBar, Spinner } from "@performyard/py-components";

import Text from "../../../../../../../components/Text";
import Tooltip from "../../../../../../../components/Tooltip";
// components
import { CardBody, CardHeader } from "../../../../../components/Card";

// overview store
import useStore from "../../../../../store";

// hooks
import { useNPSSummary } from "../../../../../../../api/hooks";

// utils
import { getColor } from "../../../../../../../utils/getColor";

// config
import Config from "../../../../../Config";
// styles
import css from "./enpsKPI.module.scss";

const enpsKPI = () => {
	const [filter] = useStore((state) => [state.filter]);

	const binSize = Config.BIN_SIZE;

	const navigate = useNavigate();

	const { data, isLoading, error } = useNPSSummary({
		startDate: filter.startDate,
		endDate: filter.endDate || endOfDay(new Date()),
		binSize,
	});

	const npsData = data ? data.data[0] : null;

	const getWholeNumPercentage = (breakDownValue?: number) => {
		// Explicitly check for null or undefined. We don't want to check for falsy values because 0 is a valid value.
		const npsDataIsNullOrUndefined = npsData === undefined || npsData === null;
		const breakDownValueIsNullOrUndefined = breakDownValue === undefined || breakDownValue === null;

		if (npsDataIsNullOrUndefined || breakDownValueIsNullOrUndefined) return 0;

		return Math.round((breakDownValue / npsData.count) * 100);
	};

	const eNPSScore = npsData ? Math.round(npsData.eNPS_score) : null;

	const engagementCategories = [
		{
			id: "Promoters",
			color: getColor("green", 5),
			value: getWholeNumPercentage(npsData?.promoters),
		},
		{
			id: "Passives",
			color: getColor("yellow", 5),
			value: getWholeNumPercentage(npsData?.neutrals),
		},
		{
			id: "Detractors",
			color: getColor("red", 5),
			value: getWholeNumPercentage(npsData?.detractors),
		},
	];

	return (
		<>
			<CardHeader
				title={
					<span>
						<Text element="span" size="paragraph-3" weight="regular">
							eNPS
						</Text>{" "}
						<Text element="span" size="paragraph-3" weight="bold">
							Score
						</Text>
					</span>
				}
				info="eNPS Score is the percentage of promoters minus the percentage of detractors."
				options={[
					{
						id: "view-enps",
						label: "eNPS Details",
						// navigate to /engagement/enps page
						action: () => navigate(Config.ROUTES.ENPS.url),
					},
				]}
				style={{ paddingBottom: "16px" }}
			/>
			<CardBody>
				{error !== null ? (
					<div className={css.errorContainer}>
						<Text size="paragraph-3" weight="regular">
							{" "}
							Error fetching score
						</Text>
					</div>
				) : (
					<div className={css.kpiContainer}>
						{isLoading ? (
							<span className={css.spinRow}>
								<Spinner size={20} />
							</span>
						) : (
							<Text size="heading-1" weight="bold" testID="score">
								{eNPSScore}
							</Text>
						)}
						{isLoading ? (
							<span className={css.spinRow}>
								<Spinner size={20} />
							</span>
						) : (
							<div className={css.progressContainer}>
								{engagementCategories.map((category) => (
									<div key={category.id} className="grid grid-cols-[24px_1fr] items-center gap-2">
										<span style={{ color: category.color }}>{category.value}%</span>
										<Tooltip message={`${category.id}: ${category.value}%`} placement="bottom">
											<div style={{ width: 97 }}>
												<ProgressBar
													value={category.value}
													colorOverride={category.color}
													size={8}
												/>
											</div>
										</Tooltip>
									</div>
								))}
							</div>
						)}
					</div>
				)}
			</CardBody>
		</>
	);
};

export default enpsKPI;
